/**
 * i18n Configuration
 *
 * Configures i18next with:
 * - Shopify Admin locale for embedded routes
 * - Browser detection and persistence for public and demo routes
 * - Type-safe namespaces
 * - Optimized for React 19 + Vite
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import { LANGUAGE_STORAGE_KEY } from './constants'
import { resolveLanguagePolicy, type SupportedLanguage } from './language-policy'
import zhTW from './locales/zh-TW'
import en from './locales/en'

/**
 * Supported languages
 */
export const LANGUAGES = {
  'zh-TW': { name: '繁體中文', flag: '🇹🇼' },
  en: { name: 'English', flag: '🇺🇸' },
} as const

export type { SupportedLanguage } from './language-policy'

const SUPPORTED_LANGUAGE_CODES = Object.keys(LANGUAGES) as SupportedLanguage[]

const guidanceSurface = typeof document === 'undefined'
  ? undefined
  : document.querySelector<HTMLMetaElement>('meta[name="acr-guidance-surface"]')?.content
const languagePolicy = resolveLanguagePolicy(
  guidanceSurface,
  typeof window === 'undefined' ? undefined : window.shopify?.config.locale,
)

/**
 * Default language
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'en'

// Every surface declares the language it renders, so assistive technology
// reads Traditional Chinese as Chinese. Registered before init, which emits
// the first change synchronously with bundled resources.
if (typeof document !== 'undefined') {
  i18n.on('languageChanged', () => {
    document.documentElement.lang = i18n.resolvedLanguage ?? DEFAULT_LANGUAGE
  })
}

/**
 * Initialize i18next
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      'zh-TW': zhTW,
      en: en,
    },
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    fallbackLng: DEFAULT_LANGUAGE,
    ...(languagePolicy.language ? { lng: languagePolicy.language } : {}),
    defaultNS: 'common',
    ns: ['common', 'landing', 'billing', 'guidance'],

    // Surface-aware language detection and persistence
    detection: {
      order: languagePolicy.detectionOrder,
      caches: languagePolicy.caches,
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },

    // React options
    react: {
      useSuspense: false, // Disable suspense for React 19 compatibility
    },

    // Interpolation options
    interpolation: {
      escapeValue: false, // React already handles XSS protection
      // Every interpolated number reads in the merchant's locale (1,701,614),
      // so a large store's counts never render as raw digits.
      alwaysFormat: true,
      format: (value: unknown, _format: string | undefined, lng: string | undefined) =>
        typeof value === 'number' ? new Intl.NumberFormat(lng).format(value) : String(value),
    },

    // Development options
    debug: false,
    showSupportNotice: false,
  })

export default i18n
